import { IdeaListResponseSchema, type IdeaOrder, type IdeaSort, type IdeaStatus } from '@cult/schemas';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CreatedRangeParams } from '../../../components';
import { apiFetch } from '../../../lib/api-client';

export type IdeaFilters = CreatedRangeParams & {
  status?: IdeaStatus;
  category?: string;
  author?: string;
  q?: string;
  sort?: IdeaSort;
  order?: IdeaOrder;
  adminView?: boolean;
  limit?: number;
};

export const buildIdeaParams = (filters: IdeaFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.category) params.set('category', filters.category);
  if (filters.author) params.set('author', filters.author);
  if (filters.q) params.set('q', filters.q);
  if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
  if (filters.createdTo) params.set('createdTo', filters.createdTo);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.order) params.set('order', filters.order);
  if (filters.adminView) params.set('adminView', 'true');
  if (filters.limit != null) params.set('limit', String(filters.limit));
  return params;
};

export const ideasKey = (filters: IdeaFilters) => ['ideas', filters] as const;

export const useIdeas = (filters: IdeaFilters = {}) => {
  return useInfiniteQuery({
    queryKey: ideasKey(filters),
    queryFn: async ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) => {
      const params = buildIdeaParams(filters);
      if (pageParam) params.set('cursor', pageParam);
      const search = params.toString();
      const raw = await apiFetch<unknown>(`/ideas${search ? `?${search}` : ''}`, { signal });
      return IdeaListResponseSchema.parse(raw);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
};

export const ideasPagedKey = (filters: IdeaFilters, page: number, pageSize: number) =>
  ['ideas', 'paged', filters, page, pageSize] as const;

export const useIdeasPaged = (filters: IdeaFilters, page: number, pageSize: number) => {
  const offset = (page - 1) * pageSize;
  return useQuery({
    queryKey: ideasPagedKey(filters, page, pageSize),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const params = buildIdeaParams(filters);
      params.set('limit', String(pageSize));
      params.set('offset', String(offset));
      const raw = await apiFetch<unknown>(`/ideas?${params.toString()}`, { signal });
      return IdeaListResponseSchema.parse(raw);
    },
    placeholderData: keepPreviousData,
  });
};
